import { useAppSelector } from '@features';
import {
    Button,
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
} from '@repo/shadcn';
import { format } from 'date-fns';
import { Calendar, Check, ChevronDown, Package } from 'lucide-react';
import { useState } from 'react';

import { formatCurrencyInBDT } from '@/utils';

import { purchaseReceipt } from './purchase-print';

export const PurchaseInvoice = () => {
    const { modalData } = useAppSelector((state) => state.eventSlice);
    const [isOpen, setIsOpen] = useState(false);

    // Calculate totals
    const totalQuantity = modalData.original.items.reduce(
        (sum: any, item: any) => sum + item.quantity,
        0,
    );
    const totalAmount = modalData.original.items.reduce(
        (sum: any, item: any) => sum + item.purchase_price * item.quantity,
        0,
    );

    const submit = () => {
        purchaseReceipt(
            {
                orderNumber: modalData.original.id,
                date: new Date().toLocaleString(),
                items: modalData.original.items,
                subtotal: totalAmount,
                tax: 0,
                total: totalAmount,
                paymentMethod: 'Hand Cash',
                paymentStatus: modalData.original.payment.status,
            },
            'print',
        );
    };

    return (
        <Card className="mx-auto w-full max-w-4xl">
            <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2 text-xl">
                    <Package className="h-5 w-5" />
                    Invoice Summary
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[50%]">Item</TableHead>
                            <TableHead className="text-right">
                                Unit Price
                            </TableHead>
                            <TableHead className="text-right">
                                Quantity
                            </TableHead>
                            <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {modalData.original.items.map((item: any) => (
                            <TableRow key={item.id}>
                                <TableCell className="font-medium">
                                    <div>
                                        {item.product.name}
                                        <div className="mt-1 text-xs text-muted-foreground">
                                            <Collapsible
                                                open={isOpen}
                                                onOpenChange={setIsOpen}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-3 w-3" />
                                                    Expires:{' '}
                                                    {format(
                                                        new Date(
                                                            item.expiration_date,
                                                        ),
                                                        'MMM d, yyyy',
                                                    )}
                                                    <CollapsibleTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 p-0"
                                                        >
                                                            <ChevronDown className="h-3 w-3" />
                                                            <span className="sr-only">
                                                                Toggle details
                                                            </span>
                                                        </Button>
                                                    </CollapsibleTrigger>
                                                </div>
                                                <CollapsibleContent>
                                                    <div className="mt-2 space-y-1">
                                                        <div className="flex items-center gap-2 text-xs">
                                                            <span className="text-muted-foreground">
                                                                ID:
                                                            </span>{' '}
                                                            {item.id}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs">
                                                            <span className="text-muted-foreground">
                                                                Purchase ID:
                                                            </span>{' '}
                                                            {item.purchase_id}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs">
                                                            <span className="text-muted-foreground">
                                                                Created:
                                                            </span>{' '}
                                                            {format(
                                                                new Date(
                                                                    item.created_at,
                                                                ),
                                                                'MMM d, yyyy HH:mm',
                                                            )}
                                                        </div>
                                                    </div>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="text-right">
                                    {formatCurrencyInBDT(
                                        item.purchase_price.toFixed(2),
                                    )}
                                </TableCell>
                                <TableCell className="text-right">
                                    {item.quantity}
                                </TableCell>
                                <TableCell className="text-right">
                                    {formatCurrencyInBDT(
                                        Number(
                                            (
                                                item.purchase_price *
                                                item.quantity
                                            ).toFixed(2),
                                        ),
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={2}>Total</TableCell>
                            <TableCell className="text-right">
                                {totalQuantity}
                            </TableCell>
                            <TableCell className="text-right">
                                {formatCurrencyInBDT(totalAmount.toFixed(2))}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </CardContent>
            <CardFooter className="flex justify-between border-t p-4">
                <div className="text-sm text-muted-foreground">
                    {modalData.original.items.length}{' '}
                    {modalData.original.items.length === 1 ? 'item' : 'items'}{' '}
                    in this invoice
                </div>
                <Button onClick={submit} size="sm" className="gap-1">
                    <Check className="h-4 w-4" /> Print Invoice
                </Button>
            </CardFooter>
        </Card>
    );
};

PurchaseInvoice.displayName = 'PurchaseInvoice';
